import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { ReactSketchCanvas } from "react-sketch-canvas";

const QuestionCanvas = forwardRef(function QuestionCanvas(
  { imageSrc, onExport, containerRef },
  ref,
) {
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const [imageRatio, setImageRatio] = useState(null);
  const [canvasHeight, setCanvasHeight] = useState(600);

  useEffect(() => {
    const updateCanvasHeight = () => {
      const containerWidth =
        containerRef?.current?.getBoundingClientRect?.().width ||
        imageRef?.current?.getBoundingClientRect?.().width ||
        0;
      const safeWidth = Math.max(containerWidth, 320);

      if (imageRatio) {
        setCanvasHeight(safeWidth * imageRatio);
        return;
      }

      setCanvasHeight(600);
    };

    updateCanvasHeight();
    window.addEventListener("resize", updateCanvasHeight);

    return () => window.removeEventListener("resize", updateCanvasHeight);
  }, [containerRef, imageRatio]);

  const exportImage = async () => {
    if (!canvasRef.current) return null;

    try {
      const paths = await canvasRef.current.exportPaths();
      const hasPaths = paths?.some(
        (path) => path?.drawMode !== false && path?.paths?.length > 0,
      );

      if (!hasPaths) {
        onExport?.(null);
        return null;
      }

      const dataUrl = await canvasRef.current.exportImage("png");

      const res = await fetch(dataUrl);
      const blob = await res.blob();

      const file = new File([blob], "question-correction.png", {
        type: "image/png",
        lastModified: Date.now(),
      });

      onExport?.(file);
      return file;
    } catch (error) {
      console.error("Failed to export canvas image:", error);
      return null;
    }
  };

  const clearDrawing = async () => {
    await canvasRef.current?.clearCanvas();
    onExport?.(null);
  };

  useImperativeHandle(ref, () => ({
    exportImage,
  }));

  return (
    <div className="h-full w-full">
      <div
        className="relative w-full overflow-hidden rounded-lg"
        style={{ height: `${canvasHeight}px` }}
      >
        <img
          ref={imageRef}
          src={imageSrc}
          alt="Question draft preview"
          className="absolute inset-0 h-full w-full object-contain"
          onLoad={(event) => {
            const naturalWidth = event.currentTarget.naturalWidth || 1;
            const naturalHeight = event.currentTarget.naturalHeight || 1;

            setImageRatio(naturalHeight / naturalWidth);
          }}
        />

        <div className="absolute inset-0">
          <ReactSketchCanvas
            ref={canvasRef}
            width="100%"
            height="100%"
            strokeWidth={4}
            strokeColor="black"
            canvasColor="transparent"
            preserveBackgroundImageAspectRatio="meet"
            style={{ border: "none", background: "transparent" }}
            svgStyle={{ background: "transparent" }}
          />
        </div>
      </div>

      <div className="mt-3 flex gap-3">
        <button
          type="button"
          className="submit mt-3 w-full"
          onClick={exportImage}
        >
          Save Drawing
        </button>

        <button
          type="button"
          className="submit mt-3 w-full"
          onClick={() => canvasRef.current?.undo()}
        >
          Undo
        </button>

        <button
          type="button"
          className="submit mt-3 w-full"
          onClick={clearDrawing}
        >
          Clear
        </button>
      </div>
    </div>
  );
});

export default QuestionCanvas;
